import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { PutVendorTaxRequest, TinType, Vendor, VendorTax } from "@/api/types";
import { TIN_TYPES } from "@/lib/vendorTax";
import { updateVendorTax } from "@/lib/vendorTaxStore";
import { DialogShell } from "./DialogShell";
import { StateMessage } from "./StateMessage";
import "./VendorTaxDialog.css";

const fields = [
  ["legalName", "Legal name"], ["addressLine1", "Address line 1"],
  ["addressLine2", "Address line 2"], ["city", "City"], ["state", "State"], ["zip", "ZIP"],
] as const;
function profileOf(tax?: VendorTax): PutVendorTaxRequest {
  return {
    legalName: tax?.legalName ?? "", addressLine1: tax?.addressLine1 ?? "",
    addressLine2: tax?.addressLine2 ?? "", city: tax?.city ?? "",
    state: tax?.state ?? "", zip: tax?.zip ?? "", form1099: tax?.form1099 ?? "nec",
  };
}

export function VendorTaxDialog({ vendor, initialTax, onClose, onChanged }: {
  vendor: Vendor;
  initialTax?: VendorTax;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [tax, setTax] = useState(initialTax);
  const [profile, setProfile] = useState(() => profileOf(initialTax));
  const [loading, setLoading] = useState(!initialTax);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"none" | "on-file" | "replacing">(initialTax?.tin.hasTin ? "on-file" : "none");
  const [tinType, setTinType] = useState<TinType>(initialTax?.tin.tinType ?? "ssn");
  const [typedTin, setTypedTin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tinError, setTinError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const abort = useRef<AbortController | null>(null);
  const id = useId();
  const formId = `${id}-form`;

  useEffect(() => {
    const request = new AbortController();
    abort.current = request;
    if (!initialTax) {
      void demoApi.getVendorTax(vendor.id, request.signal).then((value) => {
        if (request.signal.aborted) return;
        setTax(value);
        setProfile(profileOf(value));
        setTinType(value.tin.tinType ?? "ssn");
        setMode(value.tin.hasTin ? "on-file" : "none");
        updateVendorTax(value);
      }).catch((err: unknown) => {
        if (!request.signal.aborted) setError(problemMessage(err, "Could not load tax info."));
      }).finally(() => { if (!request.signal.aborted) setLoading(false); });
    }
    // No draft survives unmount. Abort any pending request; component state is discarded.
    return () => { request.abort(); };
    // The initial server snapshot seeds this one dialog opening, never a later store update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.id]);

  function close(): void {
    setTypedTin("");
    onClose();
  }
  function remember(value: VendorTax): void {
    setTax(value);
    updateVendorTax(value);
    onChanged?.();
  }
  function failedMessage(err: unknown, fallback: string): string {
    return err instanceof ApiError && err.status === 0 ? "Not saved. No connection; enter the tax ID again when you are online." : problemMessage(err, fallback);
  }

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || loading || !tax) return;
    // The only temporary copy is in this request stack; it is never placed in a store.
    let pendingTin = typedTin;
    setTypedTin("");
    setError(null);
    setTinError(null);
    setFieldErrors(null);
    if (!navigator.onLine) {
      pendingTin = "";
      setError("Not saved. No connection; enter the tax ID again when you are online.");
      return;
    }
    setBusy(true);
    let writingTin = false;
    const signal = abort.current!.signal;
    try {
      const saved = await demoApi.putVendorTax(vendor.id, profile, signal);
      if (signal.aborted) return;
      remember(saved);
      if (pendingTin.trim() !== "") {
        writingTin = true;
        const result = await demoApi.putVendorTaxId(vendor.id, { tinType, tin: pendingTin }, signal);
        if (signal.aborted) return;
        remember(result);
      }
      close();
    } catch (err: unknown) {
      if (signal.aborted) return;
      const problems = problemFieldErrors(err);
      if (writingTin) setTinError(problems?.tin?.[0] ?? problems?.tinType?.[0] ?? failedMessage(err, "Tax ID not saved."));
      else {
        setFieldErrors(problems);
        setError(failedMessage(err, "Tax info not saved."));
      }
    } finally {
      pendingTin = "";
      if (!signal.aborted) setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setRemoveError(null);
    setTypedTin("");
    const signal = abort.current!.signal;
    try {
      const value = await demoApi.deleteVendorTaxId(vendor.id, signal);
      if (signal.aborted) return;
      remember(value);
      setMode("none");
      setTinError(null);
      setConfirmRemove(false);
    } catch (err: unknown) {
      if (!signal.aborted) setRemoveError(failedMessage(err, "Tax ID was not removed."));
    } finally { if (!signal.aborted) setBusy(false); }
  }

  return (
    <>
      <DialogShell title={`Tax info - ${vendor.name}`} size="sm" onClose={close} busy={busy || confirmRemove} restoreFocus
        footer={<>
          <button type="button" className="btn btn--ghost" disabled={busy || confirmRemove} onClick={close}>Cancel</button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={busy || loading || !tax || confirmRemove}>Save tax info</button>
        </>}>
        {loading ? <StateMessage tone="loading" title="Loading tax info..." /> :
          <form id={formId} className="vendor-tax-dialog" onSubmit={(event) => void save(event)} noValidate>
            {fields.map(([field, label]) => <div className="vendor-tax-dialog__field" key={field}>
              <label htmlFor={`${id}-${field}`}>{label}</label>
              <input id={`${id}-${field}`} value={profile[field] ?? ""} disabled={busy || !tax}
                aria-invalid={fieldErrors?.[field] ? true : undefined}
                onChange={(event) => setProfile((current) => ({ ...current, [field]: event.target.value }))} />
            </div>)}
            <label className="vendor-tax-dialog__exempt">
              <input type="checkbox" checked={profile.form1099 === "exempt"} disabled={busy || !tax}
                onChange={(event) => setProfile((current) => ({ ...current, form1099: event.target.checked ? "exempt" : "nec" }))} />
              Exempt from 1099
            </label>
            {error && <p className="vendor-tax-dialog__error" role="alert">{error}</p>}
            <div className="vendor-tax-dialog__tin" role="group" aria-label="Tax ID" data-state={mode}>
              {mode === "on-file" ? <>
                <p>Tax ID on file: {tax?.tin.masked}</p>
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setTypedTin(""); setTinError(null); setMode("replacing"); }}>Replace tax ID</button>
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setRemoveError(null); setConfirmRemove(true); }}>Remove tax ID</button>
              </> : <>
                <div className="vendor-tax-dialog__field">
                  <label htmlFor={`${id}-type`}>Tax ID type</label>
                  <select id={`${id}-type`} value={tinType} disabled={busy || !tax} onChange={(event) => setTinType(event.target.value as TinType)}>
                    {TIN_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                </div>
                <div className="vendor-tax-dialog__field">
                  <label htmlFor={`${id}-tin`}>Tax ID</label>
                  <input id={`${id}-tin`} type="password" autoComplete="off" inputMode="numeric" value={typedTin}
                    disabled={busy || !tax} aria-invalid={tinError ? true : undefined} aria-describedby={tinError ? `${id}-tin-error` : undefined}
                    onChange={(event) => { setTypedTin(event.target.value); setTinError(null); }} />
                </div>
                {mode === "replacing" && <button type="button" className="btn btn--ghost" disabled={busy}
                  onClick={() => { setTypedTin(""); setTinError(null); setMode("on-file"); }}>Keep current tax ID</button>}
              </>}
              {tinError && <p id={`${id}-tin-error`} className="vendor-tax-dialog__error" role="alert">{tinError}</p>}
            </div>
          </form>}
      </DialogShell>
      {confirmRemove && <DialogShell title="Remove tax ID?" role="alertdialog" size="sm" busy={busy}
        initialFocus={keep} describedBy={`${id}-remove-description`} restoreFocus onClose={() => setConfirmRemove(false)} footer={<>
          <button ref={keep} type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirmRemove(false)}>Keep it</button>
          <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void remove()}>Yes, remove</button>
        </>}>
        <p id={`${id}-remove-description`}>Remove the tax ID on file for {vendor.name}?</p>
        {removeError && <p className="vendor-tax-dialog__error" role="alert">{removeError}</p>}
      </DialogShell>}
    </>
  );
}
